import { useEffect, useMemo, useRef } from 'react';

import { Animated, Easing, Platform } from 'react-native';

export default function useRotateAnimation({
  isRotated,
  fromDeg,
  toDeg,
}: {
  isRotated: boolean;
  fromDeg: number;
  toDeg: number;
}) {
  const rotateAnimation = useRef(new Animated.Value(isRotated ? 1 : 0)).current;
  const previous = useRef({ isRotated, fromDeg, toDeg });
  const rotateData = useMemo(
    () =>
      rotateAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [`${fromDeg}deg`, `${toDeg}deg`],
      }),
    [rotateAnimation, fromDeg, toDeg]
  );

  useEffect(() => {
    const last = previous.current;
    previous.current = { isRotated, fromDeg, toDeg };
    if (last.isRotated === isRotated && last.fromDeg === fromDeg && last.toDeg === toDeg) {
      return;
    }

    rotateAnimation.setValue(isRotated ? 0 : 1);
    const animation = Animated.timing(rotateAnimation, {
      toValue: isRotated ? 1 : 0,
      duration: (Math.abs(toDeg - fromDeg) / 800) * 1000,
      useNativeDriver: Platform.OS !== 'web',
      easing: Easing.linear,
    });
    animation.start();
    return () => animation.stop();
  }, [isRotated, fromDeg, toDeg, rotateAnimation]);

  return { rotateData };
}
